import { createContext, useContext } from 'react';

export interface TenantAuthConfig {
  candidate_welcome?: string | null;
  candidate_tagline?: string | null;
  employer_welcome?: string | null;
  employer_tagline?: string | null;
  panel_bg?: string | null;
}

export interface TenantContextValue {
  /** Active tenant theme name (e.g. 'wfg', 'nlctb', 'default'). */
  theme: string;
  /** Active tenant identifier (e.g. 'wfg', 'nlctb'). May differ from theme. */
  tenant: string;
  /** Human-readable tenant name (e.g. 'Work For Good'), from tenant.display_name.
   *  Null when the tenant has no display name — callers fall back to a default. */
  displayName: string | null;
  /** URL to tenant logo image (from DB), or null for built-in SVG logos. */
  logoUrl: string | null;
  /** URL to tenant logo variant for dark backgrounds (from DB), or null. */
  logoUrlDark: string | null;
  /** Auth page configuration (welcome text, tagline, panel background). */
  authConfig: TenantAuthConfig | null;
  /** Default logo image for organizations without their own logo. */
  defaultOrgLogoUrl: string | null;
  /** Social-share image (og:image) URL for this tenant, resolved from
   *  email_config.share_image. Null falls back to the bundled default. */
  shareImageUrl: string | null;
}

const TenantContext = createContext<TenantContextValue>({ theme: 'default', tenant: 'default', displayName: null, logoUrl: null, logoUrlDark: null, authConfig: null, defaultOrgLogoUrl: null, shareImageUrl: null });

export const TenantProvider = TenantContext.Provider;

/** Returns the current tenant theme name. */
export function useTenantTheme(): string {
  return useContext(TenantContext).theme;
}

/** Returns the full tenant context (theme + tenant name + logoUrl). */
export function useTenant(): TenantContextValue {
  return useContext(TenantContext);
}

/** Returns the human-readable tenant name (e.g. 'Work For Good'), or null. */
export function useTenantDisplayName(): string | null {
  return useContext(TenantContext).displayName;
}

/** Returns the tenant logo URL (from DB), or null for built-in SVG logos. */
export function useTenantLogoUrl(): string | null {
  return useContext(TenantContext).logoUrl;
}

/** Returns the tenant dark-background logo URL (from DB), or null. */
export function useTenantLogoUrlDark(): string | null {
  return useContext(TenantContext).logoUrlDark;
}

/** Returns the tenant auth page configuration. */
export function useTenantAuthConfig(): TenantAuthConfig | null {
  return useContext(TenantContext).authConfig;
}

/** Returns the tenant's default org logo URL, or null to use global default. */
export function useTenantDefaultOrgLogoUrl(): string | null {
  return useContext(TenantContext).defaultOrgLogoUrl;
}

/** Returns the tenant's social-share image (og:image) URL, or null. */
export function useTenantShareImageUrl(): string | null {
  return useContext(TenantContext).shareImageUrl;
}
